import { DefaultSelection } from "@components/ScenarioTree"
import { GraphDto } from "@custom-types/graphs"
import {
  Box,
  CircularProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material"
import { skipToken } from "@reduxjs/toolkit/query"
import { useGetFamilyBasesQuery } from "@store/services/baseGraphFamiliesService"
import { useEffect, useState } from "react"
import { getSortedGraphBases } from "utils/graph"

type SelectBaseGraphProps = {
  familyKey: string
  defaultSelection: DefaultSelection | undefined
  onChange: (base: GraphDto) => void
}

const SelectBaseGraph = ({ familyKey, defaultSelection, onChange }: SelectBaseGraphProps) => {
  const [selectedBase, setSelectedBase] = useState<GraphDto>()
  const [sortedBases, setSortedBases] = useState<GraphDto[]>([])
  const [isDefaultSelectionApplied, setIsDefaultSelectionApplied] = useState(false)

  const { data: basesData, isFetching } = useGetFamilyBasesQuery(
    familyKey ? { familyKey: familyKey, roleHint: "whatif" } : skipToken,
  )

  useEffect(() => {
    if (!basesData) return

    const bases = getSortedGraphBases(basesData)
    setSortedBases(bases)

    const baseWithHighestVersion = bases[0]
    const baseWithDefaultSelectionVersion =
      defaultSelection && !isDefaultSelectionApplied
        ? basesData.find((base) => base.version === defaultSelection.version)
        : null
    const baseToSelect = baseWithDefaultSelectionVersion ?? baseWithHighestVersion
    if (baseToSelect) setSelectedBase(baseToSelect)

    setIsDefaultSelectionApplied(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [basesData])

  useEffect(() => {
    if (!selectedBase) return

    onChange(selectedBase)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [selectedBase])

  if (isFetching) return <CircularProgress />
  if (!familyKey) return

  const handleChange = ({ target: { value } }: SelectChangeEvent<string>) => {
    const base = sortedBases.find(({ key }) => key === value)
    if (!base) return

    setSelectedBase(base)
  }

  return (
    <Box>
      <Typography component="span" variant="h5" sx={{ p: 0, m: 0, verticalAlign: "middle" }}>
        <b>{selectedBase ? `${selectedBase.name}` : ""}</b>
      </Typography>
      <Select
        sx={{
          boxShadow: "none",
          p: 0,
          ".MuiOutlinedInput-input": {
            p: 0,
            pl: "10px",
            pr: "0px",
          },
          "&:hover": { backgroundColor: "#f7f7f7" },
          ".MuiOutlinedInput-notchedOutline": { border: 0 },
          "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            border: 0,
          },
          "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: 0,
          },
        }}
        size="small"
        value={selectedBase ? selectedBase.key : ""}
        onChange={handleChange}
      >
        {sortedBases.map((base) => (
          <MenuItem key={base.key} value={base.key}>
            <Typography component="span" variant="h6" sx={{ mr: "3px", verticalAlign: "middle" }}>
              {base.version}
            </Typography>
          </MenuItem>
        ))}
      </Select>
    </Box>
  )
}

export default SelectBaseGraph
